import React from 'react';
import { Box, Typography, Card, CardContent, CardActionArea, Chip } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import { formatDate, LOCALE_MAP } from 'utils/locale-formatting';

const VISIBILITY_STYLE = {
  'public': { color: '#4caf50', bg: '#e8f5e9' },
  'protected': { color: '#1976d2', bg: '#e3f2fd' },
  'private': { color: '#757575', bg: '#f5f5f5' },
};

const STATUS_KEY = {
  'Published': 'published',
  'Draft': 'draft',
  'Under Review': 'review',
  'Approved': 'approved',
  'Archived': 'archived',
};

/**
 * PolicyCard - Policy summary card with visibility indicator
 */
export const PolicyCard = ({
  name,
  version,
  status,
  visibility,
  lastUpdated,
  onClick,
}) => {
  const { t, i18n } = useTranslation('transparency');
  const locale = LOCALE_MAP[i18n.language] ?? 'en-US';
  const visKey = VISIBILITY_STYLE[visibility] ? visibility : 'private';
  const visStyle = VISIBILITY_STYLE[visKey];
  const visLabel = t(`shared.policy.visibility.${visKey}`);

  const statusKey = STATUS_KEY[status] || 'draft';
  const statusLabel = t(`shared.policy.status.${statusKey}`);

  const CardWrapper = onClick ? CardActionArea : Box;

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        transition: 'box-shadow 0.2s',
        '&:hover': onClick ? { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } : {},
      }}
    >
      <CardWrapper onClick={onClick}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <DescriptionIcon sx={{ color: '#1976d2', mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={visLabel}
                  size="small"
                  sx={{
                    bgcolor: visStyle.bg,
                    color: visStyle.color,
                    fontWeight: 500,
                    fontSize: '0.7rem',
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('shared.policy.version', { version })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {statusLabel}
                </Typography>
              </Box>
              {lastUpdated && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {t('shared.policy.lastUpdated', { date: formatDate(lastUpdated, locale) })}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </CardWrapper>
    </Card>
  );
};
